'use client';

import React from 'react';
import { CollateralInputV2 } from 'src/components/Inputs/CollateralInput';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import ValueDisplay from 'src/components/ValueDisplay';
import { MIN_LEFTOVER_SOL } from 'src/constants/constants';
import Checkbox from 'src/components/primitives/Checkbox/Checkbox';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import { IF_STAKE_ACCOUNT_BASE_RENT } from '@velocity-exchange/common';
import { InputQuickActions } from 'src/components/Inputs/InputQuickActions';
import Select from 'src/components/primitives/Select/Select';
import Link from 'src/components/primitives/Link/Link';
import UnstakeRequestInlineAlert from 'src/components/Staking/UnstakeRequestInlineAlert';
import {
	StakingFormContentProps,
	UseStakeForm,
} from 'src/components/Staking/types';
import Divider from '../primitives/Divider/Divider';
import { getCleanedMarketSymbol } from 'src/utils/market';

interface Props
	extends
		Omit<
			UseStakeForm,
			'isSubmitDisabled' | 'handleSubmit' | 'submitButtonContent'
		>,
		StakingFormContentProps {}

export default function StakeFormContent({
	isDriftStaking,
	selectedMarket,
	amount,
	setAmount,
	amountBignum,
	currentCollatBalanceBigNum,
	depositableBalanceBigNum,
	subAccountKeyToStakeFrom,
	displayPrecision,
	stakeFromSelect,
	isMaxSolDeposit,
	isInsufficientSolBalance,
	selectedAccountCanBeLiqd,
	isStakingFromSubaccount,
	isGeoblocked,
	userNeedsStakeAccount,
	unstakingPeriodAsDays,
	targetCollatBalances,
	canWithdraw,
	withdrawalLimit,
	isWalletConnected,
	existingUnstakeRequestAmount,
	existingUnstakeRequestTimestamp,
	userStakeAccountBalance,
	hasExistingUnstakeRequest,
	hasAcceptedRisks,
	excludedMarkets,
	isSubmitting,
	hideUnstakeRequest,
	handleSelectedMarketChange,
	handleHasAcceptedRisksChange,
	handleCancelUnstakeRequest,
	handleWithdrawUnstakedAssets,
}: Props) {
	const {
		open,
		onOpenChange,
		value,
		onValueChange,
		selectedOption,
		selectOptions,
	} = stakeFromSelect;

	return (
		<>
			<Select.Root
				open={open}
				onOpenChange={onOpenChange}
				value={value}
				onValueChange={onValueChange}
				disabled={!isWalletConnected}
			>
				<Select.Trigger
					label="Stake from"
					valueClassName="normal-case"
					disabled={!isWalletConnected}
				>
					<span className="truncate">{selectedOption?.label}</span>
				</Select.Trigger>
				<Select.Options scrollContainerClassName="max-h-[240px]">
					{selectOptions.map((option) => (
						<Select.Option key={option.value} value={option.value}>
							{option.label}
						</Select.Option>
					))}
				</Select.Options>
			</Select.Root>

			<CollateralInputV2
				selectedMarket={selectedMarket}
				maxAmount={depositableBalanceBigNum}
				currentBalance={currentCollatBalanceBigNum}
				onChangeMarket={handleSelectedMarketChange}
				disabled={hasExistingUnstakeRequest}
				banks={[selectedMarket]}
				label={isDriftStaking ? 'Amount to stake' : 'Vault and amount to stake'}
				value={amount}
				onChangeValue={setAmount}
				amountLabel={
					isStakingFromSubaccount ? 'Account balance' : 'Wallet balance'
				}
				source={
					isStakingFromSubaccount
						? {
								type: 'userAccount',
								userKey: subAccountKeyToStakeFrom,
							}
						: {
								type: 'wallet',
							}
				}
				excludedMarketIndices={excludedMarkets}
				poolId={selectedMarket.poolId}
				customElementRight={
					<InputQuickActions.SizePresets
						onClick={(amount) => setAmount(amount.print())}
						maxAmount={depositableBalanceBigNum}
					/>
				}
				isFixedAsset
			/>

			{isStakingFromSubaccount && (
				<ValueDisplay.Default
					label={
						<DefinitionTooltip definitionId="stakingSubaccountWithdrawalLimits">
							Limit
						</DefinitionTooltip>
					}
					value={`${withdrawalLimit.prettyPrint()} ${getCleanedMarketSymbol(
						selectedMarket.symbol
					)}`}
				/>
			)}

			{isMaxSolDeposit && !isStakingFromSubaccount && (
				<p className="typo-body-xs text-text-secondary">
					We hold back {MIN_LEFTOVER_SOL.toFixed(2)} SOL on max deposits to
					cover transaction fees.
				</p>
			)}

			<div className="mt-auto flex flex-col gap-y-sm">
				<div className="flex flex-col gap-1">
					<div className="flex items-center justify-between gap-1">
						<span className="typo-body-xs text-text-secondary">
							{isStakingFromSubaccount
								? 'New Account Balance'
								: 'New Wallet Balance'}
						</span>
						<span className="typo-body-xs text-text-default">
							{isStakingFromSubaccount
								? `${targetCollatBalances.netBaseBalance
										.sub(amountBignum)
										.toFixed(displayPrecision)} ${getCleanedMarketSymbol(
										selectedMarket.symbol
									)}`
								: `${currentCollatBalanceBigNum
										.sub(amountBignum)
										.toFixed(displayPrecision)} ${getCleanedMarketSymbol(
										selectedMarket.symbol
									)}`}
						</span>
					</div>
					<div className="flex items-center justify-between gap-1">
						<span className="typo-body-xs text-text-secondary">
							New Staked Balance
						</span>
						<span className="typo-body-xs text-text-default">
							{(amountBignum.toNum() + userStakeAccountBalance).toFixed(2)}{' '}
							{getCleanedMarketSymbol(selectedMarket.symbol)}
						</span>
					</div>
				</div>

				{!hideUnstakeRequest && (
					<UnstakeRequestInlineAlert
						hasExistingUnstakeRequest={hasExistingUnstakeRequest}
						canWithdraw={canWithdraw}
						existingUnstakeRequestAmount={existingUnstakeRequestAmount}
						existingUnstakeRequestTimestamp={existingUnstakeRequestTimestamp}
						selectedMarket={selectedMarket}
						isSubmitting={isSubmitting}
						handleWithdrawUnstakedAssets={handleWithdrawUnstakedAssets}
						handleCancelUnstakeRequest={handleCancelUnstakeRequest}
						displayPrecision={displayPrecision}
					/>
				)}

				{userNeedsStakeAccount && isInsufficientSolBalance && (
					<InlineAlert variant="warning" title="Not enough SOL in your wallet">
						To stake a new asset, you need to have{' '}
						{IF_STAKE_ACCOUNT_BASE_RENT.toFixed(3)} SOL in your wallet for stake
						account creation.
					</InlineAlert>
				)}

				{isStakingFromSubaccount && selectedAccountCanBeLiqd && (
					<InlineAlert
						variant="error"
						title="Withdrawals from this account are paused"
					>
						Once the account liquidation is complete, withdrawals will be
						enabled again.
					</InlineAlert>
				)}

				{isGeoblocked && (
					<InlineAlert variant="error" title="Geoblocked">
						You are not allowed to use Drift from a restricted territory
					</InlineAlert>
				)}
			</div>

			{isWalletConnected && (
				<>
					<div className="flex flex-col gap-y-xs">
						<div className="typo-label-xs text-text-default">
							Staked funds cannot be used as collateral
						</div>
						<p className="typo-body-2xs text-text-secondary">
							If you decide to unstake, the amount will be subject to a{' '}
							{unstakingPeriodAsDays}-day cool down period.{' '}
							{hasExistingUnstakeRequest &&
								'You cannot stake while you have an unstake request in progress.'}{' '}
							<Link
								className="inline-flex"
								variant="secondary"
								href="https://docs.drift.trade/insurance-fund/insurance-fund-staking"
								openInNewTab
							>
								Learn more
							</Link>
						</p>
					</div>
				</>
			)}

			{!isGeoblocked && userNeedsStakeAccount && (
				<>
					<Divider />
					<Checkbox.Default
						id="stake-terms"
						label={
							<>
								I have read and understand the{' '}
								{isDriftStaking && (
									<>
										<Link
											href="https://docs.drift.trade/drift-safety-module"
											openInNewTab
										>
											Drift Safety Module
										</Link>{' '}
										and{' '}
									</>
								)}{' '}
								<Link
									href="https://docs.drift.trade/insurance-fund/insurance-fund-staking"
									openInNewTab
								>
									Insurance Fund Staking
								</Link>{' '}
								documentation and accept all associated risks.
							</>
						}
						checked={hasAcceptedRisks}
						labelClassName="whitespace-normal"
						onCheckedChange={handleHasAcceptedRisksChange}
					/>
				</>
			)}
		</>
	);
}
